import { supabase } from '../lib/supabase'

// Generate a unique session ID
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
}

// Get or create session ID from sessionStorage
const getSessionId = (): string => {
  const key = 'pcw_analytics_session'
  let sessionId = sessionStorage.getItem(key)
  if (!sessionId) {
    sessionId = generateSessionId()
    sessionStorage.setItem(key, sessionId)
  }
  return sessionId
}

// Detect device type
const getDeviceType = (): 'desktop' | 'tablet' | 'mobile' => {
  const width = window.innerWidth
  if (width < 768) return 'mobile'
  if (width < 1024) return 'tablet'
  return 'desktop'
}

// Get browser info
const getBrowserInfo = (): { browser: string; os: string } => {
  const ua = navigator.userAgent
  let browser = 'Unknown'
  let os = 'Unknown'

  // Detect browser
  if (ua.includes('Firefox')) browser = 'Firefox'
  else if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome'
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari'
  else if (ua.includes('Edg')) browser = 'Edge'

  // Detect OS
  if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Mac')) os = 'macOS'
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('Android')) os = 'Android'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'

  return { browser, os }
}

export interface AnalyticsEvent {
  event_type: 'page_view' | 'click' | 'active_time' | 'scroll' | 'interaction'
  event_name?: string
  page_path: string
  page_title?: string
  referrer?: string
  element_type?: string
  element_id?: string
  element_text?: string
  resource_type?: 'contribution' | 'curation' | 'brand' | 'user_profile'
  resource_id?: string
  active_time_ms?: number
  metadata?: Record<string, any>
}

class AnalyticsServiceClass {
  private sessionId: string
  private userId: string | null = null
  private sessionInitialized = false
  private eventQueue: AnalyticsEvent[] = []
  private flushTimeout: NodeJS.Timeout | null = null
  private readonly FLUSH_INTERVAL = 5000 // Flush every 5 seconds
  private readonly MAX_QUEUE_SIZE = 20

  constructor() {
    this.sessionId = getSessionId()
    this.initSession()
  }

  private async initSession() {
    if (this.sessionInitialized) return

    // Get current user
    const { data: { user } } = await supabase.auth.getUser()
    this.userId = user?.id || null

    // Listen for auth changes
    supabase.auth.onAuthStateChange((event, session) => {
      this.userId = session?.user?.id || null
    })

    // Create session record
    const { browser, os } = getBrowserInfo()
    await supabase.from('analytics_sessions').upsert({
      session_id: this.sessionId,
      user_id: this.userId,
      device_type: getDeviceType(),
      browser,
      os,
      entry_page: window.location.pathname,
      referrer: document.referrer || null,
      started_at: new Date().toISOString()
    }, {
      onConflict: 'session_id'
    })

    this.sessionInitialized = true

    // Set up periodic flush
    this.startFlushInterval()

    // Flush on page unload
    window.addEventListener('beforeunload', () => this.flush(true))
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.flush(true)
    })
  }

  private startFlushInterval() {
    if (this.flushTimeout) return
    this.flushTimeout = setInterval(() => this.flush(), this.FLUSH_INTERVAL)
  }

  private async flush(immediate = false) {
    if (this.eventQueue.length === 0) return

    const events = [...this.eventQueue]
    this.eventQueue = []

    const { browser, os } = getBrowserInfo()
    const deviceType = getDeviceType()

    const records = events.map(event => ({
      user_id: this.userId,
      session_id: this.sessionId,
      event_type: event.event_type,
      event_name: event.event_name,
      page_path: event.page_path,
      page_title: event.page_title,
      referrer: event.referrer,
      element_type: event.element_type,
      element_id: event.element_id,
      element_text: event.element_text,
      resource_type: event.resource_type,
      resource_id: event.resource_id,
      active_time_ms: event.active_time_ms,
      device_type: deviceType,
      browser,
      os,
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      metadata: event.metadata || {}
    }))

    // Use sendBeacon for immediate flushes (on page unload)
    if (immediate && typeof navigator.sendBeacon === 'function') {
      // For sendBeacon, we need to use a different approach
      // Just insert directly since sendBeacon doesn't work well with Supabase
      try {
        await supabase.from('analytics_events').insert(records)
      } catch (error) {
        console.error('Analytics flush error:', error)
      }
    } else {
      try {
        await supabase.from('analytics_events').insert(records)
      } catch (error) {
        console.error('Analytics flush error:', error)
        // Put events back in queue on failure
        this.eventQueue = [...events, ...this.eventQueue]
      }
    }
  }

  private queueEvent(event: AnalyticsEvent) {
    this.eventQueue.push(event)
    if (this.eventQueue.length >= this.MAX_QUEUE_SIZE) {
      this.flush()
    }
  }

  // Track a page view
  trackPageView(path?: string, title?: string) {
    this.queueEvent({
      event_type: 'page_view',
      page_path: path || window.location.pathname,
      page_title: title || document.title,
      referrer: document.referrer
    })
  }

  // Track a click event
  trackClick(
    elementType: string,
    elementId?: string,
    elementText?: string,
    resourceType?: AnalyticsEvent['resource_type'],
    resourceId?: string,
    metadata?: Record<string, any>
  ) {
    this.queueEvent({
      event_type: 'click',
      event_name: `click_${elementType}`,
      page_path: window.location.pathname,
      page_title: document.title,
      element_type: elementType,
      element_id: elementId,
      element_text: elementText?.substring(0, 100), // Limit text length
      resource_type: resourceType,
      resource_id: resourceId,
      metadata
    })
  }

  // Track active time on a page
  trackActiveTime(activeTimeMs: number, path?: string) {
    this.queueEvent({
      event_type: 'active_time',
      page_path: path || window.location.pathname,
      page_title: document.title,
      active_time_ms: activeTimeMs
    })
  }

  // Track a custom interaction
  trackInteraction(
    eventName: string,
    resourceType?: AnalyticsEvent['resource_type'],
    resourceId?: string,
    metadata?: Record<string, any>
  ) {
    this.queueEvent({
      event_type: 'interaction',
      event_name: eventName,
      page_path: window.location.pathname,
      page_title: document.title,
      resource_type: resourceType,
      resource_id: resourceId,
      metadata
    })
  }

  // Update session end time and stats
  async updateSession(totalActiveTimeMs: number) {
    try {
      await supabase.from('analytics_sessions').update({
        ended_at: new Date().toISOString(),
        total_active_time_ms: totalActiveTimeMs,
        exit_page: window.location.pathname,
        updated_at: new Date().toISOString()
      }).eq('session_id', this.sessionId)
    } catch (error) {
      console.error('Session update error:', error)
    }
  }

  // Get session ID for external use
  getSessionId(): string {
    return this.sessionId
  }
}

// Export singleton instance
export const AnalyticsService = new AnalyticsServiceClass()

// React hook for automatic page tracking
export function usePageTracking() {
  // This will be implemented in a separate hook file
}
